"use client";

import Image from "next/image";
import Link from "next/link";
import { useTranslations } from "next-intl";

import config from "@/config";
import { getSeoAwarePath } from "@/content/seo/locale-rollout";
import { getLocalizedSiteChrome } from "@/content/seo/site-chrome";
import logo from "@/public/images/logo.png";
import { getLocalizedPath } from "@/utils/utils";

import LanguageSwitcher from "./LanguageSwitcher";

const productLinks = [
  ["/script-to-faceless-video", "Script to video"],
  ["/url-to-faceless-video", "URL to video"],
  ["/long-form-faceless-video-generator", "Long-form video"],
  ["/faceless-video-automation", "Video automation"],
];

const footerStatements: Record<string, string> = {
  en: "Create, review, schedule, and publish from one video workflow.",
  pt: "Crie, revise, agende e publique em um único fluxo de vídeo.",
  es: "Crea, revisa, programa y publica desde un único flujo de vídeo.",
  de: "Erstellen, prüfen, planen und veröffentlichen – in einem Video-Workflow.",
  fr: "Créez, validez, planifiez et publiez depuis un seul workflow vidéo.",
  ja: "一つの動画ワークフローで、作成、確認、予約、公開まで。",
  zh: "用一个视频工作流，完成创作、审阅、排期与发布。",
  "zh-tw": "用一個影片工作流程，完成創作、審閱、排程與發佈。",
  ko: "하나의 영상 워크플로에서 제작, 검토, 예약 및 게시까지.",
  nl: "Maken, controleren, plannen en publiceren vanuit één videoworkflow.",
  pl: "Twórz, sprawdzaj, planuj i publikuj w jednym przepływie wideo.",
  ru: "Создавайте, проверяйте, планируйте и публикуйте в одном видеопроцессе.",
};

const useCaseLinks = [
  ["/faceless-youtube-video-generator", "YouTube videos"],
  ["/faceless-tiktok-video-generator", "TikTok videos"],
  ["/faceless-instagram-reels-generator", "Instagram Reels"],
  ["/faceless-podcast-video-generator", "Podcast videos"],
];

const resourceLinks = [
  ["/blog", "Faceless Video Blog"],
  ["/youtube-thumbnail-maker", "YouTube Thumbnail Maker"],
  ["/examples", "Real video examples"],
  ["/compare", "Comparisons"],
  [
    "/resources/faceless-video-script-templates",
    "Free script templates",
  ],
  [
    "/tools/faceless-video-length-calculator",
    "Video length calculator",
  ],
  ["/pricing", "Pricing"],
  ["/about", "About"],
  ["/editorial-policy", "Editorial policy"],
];

export default function Footer({ locale = "en" }: { locale?: string }) {
  const localized = getLocalizedSiteChrome(locale)?.footer;
  const productMapT = useTranslations("ProductMap");
  const statement = footerStatements[locale] || footerStatements.en;
  const footerProductLinks: string[][] = [
    ...(localized?.productLinks || productLinks),
    ["/product-map", productMapT("copy.eyebrow")],
  ];

  return (
    // Hallmark Ft5: the statement closes the narrative; the compact index preserves production routes.
    <footer className="border-t border-[var(--color-night-3)] bg-[var(--color-night)] text-[var(--color-night-ink)] [font-family:var(--font-body)]">
      <div className="mx-auto max-w-[var(--page-max)] px-5 py-14 sm:px-8 sm:py-20 lg:px-10 lg:py-24">
        <div className="max-w-5xl">
          <p className="text-xs font-bold uppercase tracking-[0.08em] text-[var(--color-night-muted)]">
            AriaFlow.ai
          </p>
          <h2 className="mt-5 max-w-[24ch] font-[family-name:var(--font-accent)] text-[clamp(2.5rem,6vw,5.5rem)] font-medium leading-[0.98] tracking-[-0.04em] text-[var(--color-night-ink)] [overflow-wrap:anywhere]">
            {statement}
          </h2>
        </div>

        <div className="mt-14 grid gap-12 border-t border-[var(--color-night-3)] pt-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20">
          <div className="max-w-sm">
            <Link
              href={getLocalizedPath("/", locale)}
              className="inline-flex min-h-11 items-center gap-3 whitespace-nowrap rounded-[var(--radius-small)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--color-focus)]"
            >
              <Image
                src={logo}
                alt={`${config.appName} logo`}
                width={36}
                height={36}
                className="rounded-[var(--radius-small)]"
              />
              <span className="text-xl font-bold tracking-[-0.025em]">AriaFlow.ai</span>
            </Link>
            <p className="mt-5 max-w-sm leading-7 text-[var(--color-night-muted)]">
              {localized?.description ||
                "Create editable faceless videos from an idea, script, or URL. Review the draft before you download, schedule, or publish it."}
            </p>
            <a
              href={`mailto:${config.mailgun.supportEmail}`}
              className="mt-4 inline-flex min-h-11 items-center whitespace-nowrap text-sm font-semibold text-[var(--color-night-ink)] underline decoration-[var(--color-night-3)] underline-offset-4 hover:decoration-[var(--color-accent-soft)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--color-focus)]"
            >
              {config.mailgun.supportEmail}
            </a>
          </div>

          <div className="grid gap-10 sm:grid-cols-3">
            <FooterColumn
              title={localized?.productTitle || "Faceless video"}
              links={footerProductLinks}
              locale={locale}
            />
            <FooterColumn
              title={localized?.useCasesTitle || "Use cases"}
              links={localized?.useCaseLinks || useCaseLinks}
              locale={locale}
            />
            <FooterColumn
              title={localized?.resourcesTitle || "Resources"}
              links={localized?.resourceLinks || resourceLinks}
              locale={locale}
            />
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-6 border-t border-[var(--color-night-3)] pt-8 text-sm text-[var(--color-night-muted)] md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            <span>© {new Date().getFullYear()} AriaFlow</span>
            <Link
              href={getLocalizedPath("/tos", locale)}
              className="whitespace-nowrap hover:text-[var(--color-night-ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--color-focus)]"
            >
              {localized?.terms || "Terms"}
            </Link>
            <Link
              href={getLocalizedPath("/privacy-policy", locale)}
              className="whitespace-nowrap hover:text-[var(--color-night-ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--color-focus)]"
            >
              {localized?.privacy || "Privacy"}
            </Link>
          </div>
          <LanguageSwitcher locale={locale} />
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({
  title,
  links,
  locale,
}: {
  title: string;
  links: string[][];
  locale: string;
}) {
  return (
    <nav aria-label={title}>
      <h2 className="text-sm font-semibold text-[var(--color-night-ink)]">{title}</h2>
      <div className="mt-5 space-y-3">
        {links.map(([path, label]) => (
          <Link
            key={path}
            href={
              path === "/product-map"
                ? getLocalizedPath(path, locale)
                : getSeoAwarePath(path, locale)
            }
            className="block text-sm leading-6 text-[var(--color-night-muted)] hover:text-[var(--color-night-ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--color-focus)]"
          >
            {label}
          </Link>
        ))}
      </div>
    </nav>
  );
}
